/**
 * Custom CRM fields panel — lists all admin-defined fields and allows the
 * current user to set per-lead values. Lazy-loads field definitions on mount.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Settings } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CustomFieldsPanel({ lead, onChanged }) {
  const [defs, setDefs] = useState([]);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showManager, setShowManager] = useState(false);

  // New-field form state
  const [nfKey, setNfKey] = useState("");
  const [nfLabel, setNfLabel] = useState("");
  const [nfType, setNfType] = useState("text");
  const [nfOptions, setNfOptions] = useState("");
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/crm/custom-fields`);
      setDefs(data.fields || []);
    } catch (_e) {
      toast.error("Failed to load custom fields");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    setValues(lead?.custom || {});
  }, [lead?.id, lead?.custom]);

  const setVal = (k, v) => setValues((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${API}/leads/${lead.id}/custom-fields`,
        { values }
      );
      setValues(data.custom || {});
      toast.success("Custom fields saved");
      onChanged?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Save failed";
      toast.error(typeof msg === "string" ? msg : "Save failed");
    } finally { setSaving(false); }
  };

  const createField = async (e) => {
    e.preventDefault();
    if (!nfKey || !nfLabel) return;
    setCreating(true);
    try {
      const opts = nfType === "select"
        ? nfOptions.split(",").map((s) => s.trim()).filter(Boolean)
        : [];
      await axios.post(`${API}/crm/custom-fields`, {
        key: nfKey, label: nfLabel, type: nfType, options: opts,
      });
      toast.success("Field created");
      setNfKey(""); setNfLabel(""); setNfType("text"); setNfOptions("");
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Create failed";
      toast.error(typeof msg === "string" ? msg : "Create failed");
    } finally { setCreating(false); }
  };

  const deleteField = async (key) => {
    if (!window.confirm(`Delete field "${key}"? This removes the value from all leads.`)) return;
    try {
      await axios.delete(`${API}/crm/custom-fields/${key}`);
      toast.success("Field deleted");
      await load();
    } catch (_e) {
      toast.error("Delete failed");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-[#3B82F6]" />
      </div>
    );
  }

  return (
    <div data-testid="ldd-custom-panel" className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wide text-[var(--al-text-muted)]">
          Custom Fields
        </span>
        <button
          type="button"
          onClick={() => setShowManager((s) => !s)}
          data-testid="ldd-custom-toggle-manager"
          className="inline-flex items-center gap-1 text-[11px] text-[#3B82F6] hover:underline"
        >
          <Settings className="w-3 h-3" /> {showManager ? "Hide" : "Manage"}
        </button>
      </div>

      {showManager && (
        <form
          onSubmit={createField}
          data-testid="ldd-custom-create-form"
          className="border border-dashed border-[var(--al-border)] rounded-lg p-3 space-y-2 bg-[var(--al-bg)]"
        >
          <div className="grid grid-cols-2 gap-2">
            <input
              type="text" placeholder="Key (e.g. budget)" value={nfKey}
              onChange={(e) => setNfKey(e.target.value)}
              data-testid="ldd-custom-new-key"
              className="al-input h-8 text-[12px]"
            />
            <input
              type="text" placeholder="Label" value={nfLabel}
              onChange={(e) => setNfLabel(e.target.value)}
              data-testid="ldd-custom-new-label"
              className="al-input h-8 text-[12px]"
            />
            <select
              value={nfType} onChange={(e) => setNfType(e.target.value)}
              data-testid="ldd-custom-new-type"
              className="al-input h-8 text-[12px]"
            >
              <option value="text">Text</option>
              <option value="number">Number</option>
              <option value="boolean">Boolean</option>
              <option value="date">Date</option>
              <option value="select">Select</option>
            </select>
            {nfType === "select" ? (
              <input
                type="text" placeholder="Options (a,b,c)" value={nfOptions}
                onChange={(e) => setNfOptions(e.target.value)}
                data-testid="ldd-custom-new-options"
                className="al-input h-8 text-[12px]"
              />
            ) : <div />}
          </div>
          <button
            type="submit" disabled={creating || !nfKey || !nfLabel}
            data-testid="ldd-custom-create-submit"
            className="h-8 px-3 rounded-md bg-[#1A1A2E] text-white text-[11.5px] font-semibold hover:bg-[#2A2A45] disabled:opacity-50 inline-flex items-center gap-1"
          >
            <Plus className="w-3 h-3" /> Create field
          </button>
        </form>
      )}

      {defs.length === 0 ? (
        <p className="text-[12px] text-[var(--al-text-muted)] italic text-center py-4">
          No custom fields yet. Click <strong>Manage</strong> to create your first one.
        </p>
      ) : (
        <div className="space-y-3">
          {defs.map((f) => {
            const v = values[f.key];
            return (
              <div key={f.key} data-testid={`ldd-custom-field-${f.key}`}>
                <div className="flex items-center justify-between text-[11px] uppercase tracking-wide text-[var(--al-text-muted)] mb-1">
                  <span>{f.label}</span>
                  {showManager && (
                    <button
                      type="button"
                      onClick={() => deleteField(f.key)}
                      data-testid={`ldd-custom-delete-${f.key}`}
                      className="text-[#DC2626] hover:opacity-80"
                      title="Delete field definition"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
                {f.type === "select" ? (
                  <select
                    value={v ?? ""} onChange={(e) => setVal(f.key, e.target.value)}
                    data-testid={`ldd-custom-input-${f.key}`}
                    className="al-input w-full h-8 text-[12.5px]"
                  >
                    <option value="">—</option>
                    {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : f.type === "boolean" ? (
                  <label className="inline-flex items-center gap-2 text-[12.5px]">
                    <input
                      type="checkbox" checked={!!v}
                      onChange={(e) => setVal(f.key, e.target.checked)}
                      data-testid={`ldd-custom-input-${f.key}`}
                    />
                    {v ? "Yes" : "No"}
                  </label>
                ) : (
                  <input
                    type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                    value={v ?? ""}
                    onChange={(e) => setVal(f.key, e.target.value)}
                    data-testid={`ldd-custom-input-${f.key}`}
                    className="al-input w-full h-8 text-[12.5px]"
                  />
                )}
              </div>
            );
          })}
          <button
            type="button" onClick={save} disabled={saving}
            data-testid="ldd-custom-save"
            className="h-9 px-4 rounded-lg bg-[#3B82F6] text-white text-[12.5px] font-semibold hover:bg-[#2563EB] disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            Save values
          </button>
        </div>
      )}
    </div>
  );
}
